import { useState }   from "react";
import {
  AlertCircle, Check, Edit2, Save, X,
  User, Mail, Phone, Briefcase, Calendar,
  FileCheck, BookOpen, DollarSign, TrendingUp, ChevronRight,
} from "lucide-react";

import Avatar        from "../shared/Avatar";
import { authHeaders }                              from "../../lib/auth";
import { formatDate, formatCurrency }               from "../../lib/format";
import { ONBOARDING_DOCS, allDocsSigned }           from "../../lib/signnow";
import { TRAINING_DATA }                            from "../../data/trainingData";
import { MOCK_GOALS }                               from "../../data/mockData";

/**
 * OverviewTab
 * Displays the user's profile card, at-a-glance stats, and an onboarding
 * warning banner when documents are still outstanding.
 *
 * Props:
 *   user       – current user object
 *   onEditSave – callback(updatedFields) called after a successful PATCH
 */
export default function OverviewTab({ user, onEditSave }) {
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({
    full_name: user?.full_name ?? "",
    phone:     user?.phone     ?? "",
    title:     user?.title     ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(false);
  const [error,  setError]  = useState("");

  const signedCount   = ONBOARDING_DOCS.filter(d => user?.onboarding?.[d.onboardingKey]).length;
  const totalDocs     = ONBOARDING_DOCS.length;
  const onboardingPct = Math.round((signedCount / totalDocs) * 100);

  async function handleSave() {
    setSaving(true); setError("");
    try {
      const res = await fetch("/api/users", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Save failed");
      }
      setSaved(true);
      setEditing(false);
      onEditSave?.(form);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

      {/* Profile card */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">My Profile</span>
          {!editing ? (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
              <Edit2 size={13} /> Edit
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={handleSave}>
                <Save size={13} /> {saving ? "Saving…" : "Save"}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)}>
                <X size={13} /> Cancel
              </button>
            </div>
          )}
        </div>

        <div className="card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 24 }}>
            <Avatar name={user?.full_name} email={user?.email} size={64} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>{user?.full_name ?? "—"}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>{user?.title ?? "Sales Representative"}</div>
              <span className={`badge badge-${user?.role ?? "sales_rep"}`} style={{ marginTop: 6, display: "inline-flex" }}>
                {(user?.role ?? "sales_rep").replace("_", " ")}
              </span>
            </div>
          </div>

          {error && <p className="settings-error"><AlertCircle size={14} />{error}</p>}
          {saved && <p className="settings-success"><Check size={14} />Profile updated</p>}

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><User size={13} style={{ display: "inline", marginRight: 5 }} />Full Name</label>
              {editing
                ? <input className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.full_name ?? "—"}</div>
              }
            </div>
            <div className="form-group">
              <label className="form-label"><Mail size={13} style={{ display: "inline", marginRight: 5 }} />Email</label>
              <div style={{ padding: "8px 0", fontSize: 13.5, color: "#94a3b8" }}>
                {user?.email ?? "—"} <span style={{ fontSize: 11 }}>(contact admin to change)</span>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><Phone size={13} style={{ display: "inline", marginRight: 5 }} />Phone</label>
              {editing
                ? <input className="form-input" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 (305) 555-0000" />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.phone ?? "—"}</div>
              }
            </div>
            <div className="form-group">
              <label className="form-label"><Briefcase size={13} style={{ display: "inline", marginRight: 5 }} />Job Title</label>
              {editing
                ? <input className="form-input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Sales Representative" />
                : <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>{user?.title ?? "—"}</div>
              }
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label"><Calendar size={13} style={{ display: "inline", marginRight: 5 }} />Date Joined</label>
              <div style={{ padding: "8px 0", fontSize: 13.5, color: "#1e293b" }}>
                {formatDate(user?.registered_at ?? user?.created_at)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* At-a-glance stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue"><FileCheck size={20} color="#2563eb" /></div>
          <div><div className="stat-label">Docs Signed</div><div className="stat-value">{signedCount}/{totalDocs}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><BookOpen size={20} color="#7c3aed" /></div>
          <div>
            <div className="stat-label">Modules Done</div>
            <div className="stat-value">
              {TRAINING_DATA.modules.filter(m => m.status === "completed").length}/{TRAINING_DATA.modules.length}
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><DollarSign size={20} color="#16a34a" /></div>
          <div><div className="stat-label">YTD Revenue</div><div className="stat-value">{formatCurrency(MOCK_GOALS.ytd_revenue, { compact: true })}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><TrendingUp size={20} color="#ea580c" /></div>
          <div><div className="stat-label">Loans Closed YTD</div><div className="stat-value">{MOCK_GOALS.ytd_loans}</div></div>
        </div>
      </div>

      {/* Onboarding warning — hidden when all docs are signed */}
      {!allDocsSigned(user?.onboarding) && (
        <div className="card" style={{ borderColor: "#fde68a", background: "#fffbeb" }}>
          <div className="card-header" style={{ borderColor: "#fde68a" }}>
            <span className="card-title" style={{ color: "#92400e" }}>
              <AlertCircle size={15} style={{ display: "inline", marginRight: 6, color: "#f59e0b" }} />
              Action Required — Complete Your Onboarding
            </span>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: "#92400e", marginBottom: 14 }}>
              You have unsigned documents. Some features are locked until onboarding is complete.
            </p>
            <div style={{ background: "#e2e8f0", borderRadius: 6, height: 6, marginBottom: 12, overflow: "hidden" }}>
              <div style={{ width: `${onboardingPct}%`, height: "100%", background: "#f59e0b", borderRadius: 6, transition: "width 0.4s" }} />
            </div>
            <p style={{ fontSize: 12, color: "#92400e", marginBottom: 14 }}>{signedCount} of {totalDocs} documents signed</p>
            <button className="btn btn-primary" onClick={() => window.location.href = "/onboarding"}>
              Continue Onboarding <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}